/**
 * Rankings & Achievements service (Phase 2.3).
 * Projections from match history + competition placements. Does not mutate Tournament Engine.
 */

import { auth } from "../firebase/config";
import * as arenaRepository from "../repositories/arenaRepository";
import * as rankingRepository from "../repositories/rankingRepository";
import * as organizationRepository from "../repositories/organizationRepository";
import * as membershipRepository from "../repositories/organizationMembershipRepository";
import { getPlayerMatchHistoryCards } from "./matchHistoryService";
import { resolvePlayer, searchPlayers } from "./identityService";
import { ensureMyPlayer } from "./playerProfileService";
import { listOrganizationBadgesForUser } from "./organizationService";
import {
  CURRENT_SEASON_KEY,
  calculatePlayerRankings,
  calculateOrganizationRankings,
  awardAchievements,
  calculateSeasonStandings,
  getLeaderboard,
  extractPlacementsFromCompetition,
  playerRankingSnapshotId,
  organizationRankingSnapshotId,
  resolveSeasonYear,
  listSeasonOptions,
  assignRanks,
  assignOrganizationRanks,
} from "../domain/rankings";
import { normalizeSportId, DEFAULT_SPORT, SPORT_IDS } from "../domain/sport/sportTypes";

function currentUid() {
  return auth.currentUser?.uid || null;
}

/**
 * Collect placements from recent competitions (read-only).
 * @param {{ max?: number }} [options]
 */
export async function collectCompetitionPlacements(options = {}) {
  const max = Math.min(options.max || 40, 60);
  const competitions = await arenaRepository.listRecentCompetitions({ max });
  /** @type {Array<{ playerId: string, placement: string, competitionId?: string, drawId?: string }>} */
  const placements = [];
  competitions.forEach((c) => {
    placements.push(...extractPlacementsFromCompetition(c));
  });
  return { competitions, placements };
}

/**
 * @param {string} playerId
 * @param {{ seasonKey?: string, publish?: boolean }} [options]
 */
export async function calculatePlayerRankingsForPlayer(playerId, options = {}) {
  const seasonKey = options.seasonKey || CURRENT_SEASON_KEY;
  const sportId = normalizeSportId(options.sportId || DEFAULT_SPORT);
  const player = await resolvePlayer(playerId);
  if (!player) throw new Error("Player not found.");

  const { items: cards } = await getPlayerMatchHistoryCards({
    playerId,
    limit: 80,
    sportId,
  });
  const { placements } = await collectCompetitionPlacements({ max: 40 });
  const badges = player.userId
    ? await listOrganizationBadgesForUser(player.userId)
    : [];
  const organizationIds = badges.map((b) => b.organizationId);

  const ranking = calculatePlayerRankings({
    playerId,
    cards,
    placements,
    seasonKey,
    profile: {
      displayName: player.displayName,
      state: player.state,
      country: player.country,
      club: player.club,
      dateOfBirth: player.dateOfBirth,
      organizationIds,
      highestPoints: player.highestRankingPoints || null,
    },
    previousBestRank: player.highestRanking || null,
  });

  if (options.publish !== false && player.userId && player.userId === currentUid()) {
    await publishPlayerRankingSnapshot(ranking, player);
  }

  return ranking;
}

/**
 * Publish denormalized public snapshot (own player only).
 */
export async function publishPlayerRankingSnapshot(ranking, player) {
  const uid = currentUid();
  if (!uid || !ranking?.playerId) return null;
  if (player?.userId && player.userId !== uid) {
    throw new Error("Can only publish your own ranking snapshot.");
  }
  const snapshotId = playerRankingSnapshotId(ranking.playerId, ranking.seasonKey);
  const payload = {
    playerId: ranking.playerId,
    userId: uid,
    displayName: ranking.displayName || player?.displayName || "Player",
    seasonKey: ranking.seasonKey,
    seasonYear: ranking.seasonYear,
    points: Math.round(ranking.overall.points || 0),
    winPct: ranking.profileStats.winPct,
    titles: ranking.profileStats.careerTitles,
    finals: ranking.profileStats.finals,
    state: ranking.stateName || null,
    country: ranking.country || null,
    organizationIds: ranking.organization?.organizationIds || [],
    ageCategory: ranking.age?.category || null,
    disciplinePoints: ranking.discipline || {},
    recentForm: ranking.profileStats.recentForm?.label || "",
    algorithmId: ranking.algorithmId,
    updatedAtMs: Date.now(),
    isProfilePublic: player?.isProfilePublic !== false,
  };
  await rankingRepository.upsertPlayerRankingSnapshot(snapshotId, payload);
  return payload;
}

/**
 * @param {string} organizationId
 * @param {{ seasonKey?: string, publish?: boolean }} [options]
 */
export async function calculateOrganizationRankingsForOrg(organizationId, options = {}) {
  const seasonKey = options.seasonKey || CURRENT_SEASON_KEY;
  const org = await organizationRepository.fetchOrganization(organizationId);
  if (!org) throw new Error("Organization not found.");

  const members = await membershipRepository.listMembershipsForOrganization(organizationId, {
    max: 80,
  });
  const active = members.filter((m) => m.status === "active");
  const { placements, competitions } = await collectCompetitionPlacements({ max: 40 });
  const orgCompIds = new Set(
    competitions.filter((c) => c.organizationId === organizationId).map((c) => c.competitionId || c.id)
  );
  const orgPlacements = placements.filter((p) => orgCompIds.has(p.competitionId));

  const memberRows = [];
  for (const m of active.slice(0, 30)) {
    if (!m.playerId) continue;
    try {
      const r = await calculatePlayerRankingsForPlayer(m.playerId, {
        seasonKey,
        publish: false,
      });
      memberRows.push({
        playerId: m.playerId,
        points: r.overall.points,
        winPct: r.profileStats.winPct,
      });
    } catch {
      /* skip */
    }
  }

  const ranking = calculateOrganizationRankings({
    organizationId,
    name: org.name,
    memberCount: org.memberCount ?? active.length,
    members: memberRows,
    placements: orgPlacements,
  });
  ranking.seasonKey = seasonKey;

  if (options.publish !== false) {
    const snapshotId = organizationRankingSnapshotId(organizationId, seasonKey);
    await rankingRepository.upsertOrganizationRankingSnapshot(snapshotId, {
      organizationId,
      name: org.name,
      seasonKey,
      totalPoints: Math.round(ranking.totalPoints || 0),
      tournamentTitles: ranking.tournamentTitles,
      podiumFinishes: ranking.podiumFinishes,
      memberCount: ranking.memberCount,
      averageWinPct: ranking.averageWinPct,
      algorithmId: ranking.algorithmId,
      updatedAtMs: Date.now(),
    });
  }

  return ranking;
}

/**
 * Public leaderboard — snapshots first, live rebuild fallback.
 * @param {object} [filters]
 */
export async function getPublicLeaderboard(filters = {}) {
  const seasonKey = filters.seasonKey || CURRENT_SEASON_KEY;
  const sportId = normalizeSportId(filters.sportId || DEFAULT_SPORT);
  const useSnapshots = sportId === SPORT_IDS.BADMINTON;
  let playerSnaps = useSnapshots
    ? await rankingRepository.listPlayerRankingSnapshots({
        seasonKey,
        max: 60,
      })
    : [];

  // If empty, seed from recent competition participants + history
  if (!playerSnaps.length) {
    playerSnaps = await rebuildLeaderboardFromCompetitions(seasonKey, sportId);
  }

  const playerRows = playerSnaps
    .filter((s) => s.isProfilePublic !== false)
    .map((s) => ({
      playerId: s.playerId,
      seasonKey: s.seasonKey,
      seasonYear: s.seasonYear,
      displayName: s.displayName,
      country: s.country,
      stateName: s.state,
      overall: { points: s.points || 0, rank: null },
      profileStats: {
        currentRanking: null,
        seasonPoints: s.points || 0,
        careerTitles: s.titles || 0,
        finals: s.finals || 0,
        winPct: s.winPct ?? null,
        recentForm: { label: s.recentForm || "—" },
      },
      organization: { organizationIds: s.organizationIds || [] },
      age: { category: s.ageCategory || "Open" },
      discipline: s.disciplinePoints || {},
      state: { state: s.state || null },
    }));

  let orgSnaps = await rankingRepository.listOrganizationRankingSnapshots({
    seasonKey,
    max: 40,
  });
  if (!orgSnaps.length) {
    orgSnaps = [];
  }
  const organizationRows = orgSnaps.map((o) => ({
    organizationId: o.organizationId,
    name: o.name,
    totalPoints: o.totalPoints || 0,
    tournamentTitles: o.tournamentTitles || 0,
    podiumFinishes: o.podiumFinishes || 0,
    memberCount: o.memberCount || 0,
    averageWinPct: o.averageWinPct ?? null,
    leaderboardPosition: null,
  }));

  return getLeaderboard({
    playerRows,
    organizationRows,
    filters: {
      seasonKey,
      discipline: filters.discipline || "",
      sportId,
      ageCategory: filters.ageCategory || "",
      organizationId: filters.organizationId || "",
      state: filters.state || "",
      country: filters.country || "",
    },
    page: { limit: filters.limit || 25, offset: filters.offset || 0 },
  });
}

/**
 * Live rebuild when snapshots empty (does not require engine changes).
 */
async function rebuildLeaderboardFromCompetitions(seasonKey, sportId = DEFAULT_SPORT) {
  const seasonYear = resolveSeasonYear(seasonKey);
  const { competitions, placements } = await collectCompetitionPlacements({ max: 30 });
  const playerIds = new Set();
  placements.forEach((p) => playerIds.add(p.playerId));
  competitions.forEach((c) => {
    (c.managedEntries || []).forEach((e) => {
      (e.memberPlayerIds || []).forEach((id) => playerIds.add(id));
      (e.members || []).forEach((m) => m.playerId && playerIds.add(m.playerId));
    });
  });

  // Also sample directory for players with history
  try {
    const searched = await searchPlayers("", 25);
    searched.forEach((p) => playerIds.add(p.playerId));
  } catch {
    /* ignore */
  }

  const rows = [];
  for (const playerId of [...playerIds].slice(0, 40)) {
    try {
      const player = await resolvePlayer(playerId);
      if (!player || player.isProfilePublic === false) continue;
      const { items: filteredCards } = await getPlayerMatchHistoryCards({
        playerId,
        limit: 40,
        sportId: sportId || null,
      });
      const ranking = calculatePlayerRankings({
        playerId,
        cards: filteredCards,
        placements: normalizeSportId(sportId || DEFAULT_SPORT) === SPORT_IDS.BADMINTON ? placements : [],
        seasonKey,
        profile: {
          displayName: player.displayName,
          state: player.state,
          country: player.country,
          club: player.club,
          dateOfBirth: player.dateOfBirth,
          organizationIds: [],
        },
      });
      if (seasonYear != null && ranking.seasonStats.matchesPlayed === 0 && ranking.overall.points === 0) {
        continue;
      }
      rows.push({
        playerId,
        displayName: player.displayName,
        seasonKey,
        seasonYear: ranking.seasonYear,
        points: ranking.overall.points,
        winPct: ranking.profileStats.winPct,
        titles: ranking.profileStats.careerTitles,
        finals: ranking.profileStats.finals,
        state: player.state || null,
        country: player.country || null,
        organizationIds: [],
        ageCategory: ranking.age.category,
        disciplinePoints: ranking.discipline,
        recentForm: ranking.profileStats.recentForm?.label || "",
        isProfilePublic: true,
      });
    } catch {
      /* skip */
    }
  }
  return rows;
}

/**
 * Profile projection for public / my-player.
 * @param {string} playerId
 * @param {{ seasonKey?: string, viewerUserId?: string|null, sportId?: string }} [options]
 */
export async function getPlayerRankingProfile(playerId, options = {}) {
  const sportId = options.sportId ? normalizeSportId(options.sportId) : undefined;
  const ranking = await calculatePlayerRankingsForPlayer(playerId, {
    seasonKey: options.seasonKey || CURRENT_SEASON_KEY,
    publish: false,
    ...(sportId ? { sportId } : {}),
  });

  // Rank among snapshot peers when available
  try {
    const board = await getPublicLeaderboard({
      seasonKey: options.seasonKey || CURRENT_SEASON_KEY,
      limit: 80,
      ...(sportId ? { sportId } : {}),
    });
    const idx = board.players.findIndex((p) => p.playerId === playerId);
    if (idx >= 0) {
      ranking.overall.rank = board.players[idx].overall?.rank ?? idx + 1;
      ranking.profileStats.currentRanking = ranking.overall.rank;
    }
  } catch {
    /* ignore */
  }

  const achievements = awardAchievements({
    cards: [], // already in ranking.achievements
    placements: [],
  });
  // Prefer ranking.achievements which was built with cards
  return {
    ...ranking,
    achievements: ranking.achievements?.length ? ranking.achievements : achievements,
    seasons: listSeasonOptions([ranking.seasonYear]),
  };
}

/**
 * Refresh current user's snapshot + optional org snapshots for memberships.
 */
export async function refreshMyRankings(seasonKey = CURRENT_SEASON_KEY) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const player = await ensureMyPlayer(uid);
  const ranking = await calculatePlayerRankingsForPlayer(player.playerId, {
    seasonKey,
    publish: true,
  });
  const badges = await listOrganizationBadgesForUser(uid);
  for (const b of badges.slice(0, 5)) {
    try {
      await calculateOrganizationRankingsForOrg(b.organizationId, {
        seasonKey,
        publish: true,
      });
    } catch {
      /* ignore */
    }
  }
  return ranking;
}

export {
  calculatePlayerRankings,
  calculateOrganizationRankings,
  awardAchievements,
  calculateSeasonStandings,
  getLeaderboard,
  assignRanks,
  assignOrganizationRanks,
  CURRENT_SEASON_KEY,
  listSeasonOptions,
};
